/**
 * The kiosk on its canvas: the scene behind, the layout of modules in front, the dialogs on top.
 * Everything here is chosen by data (preset, pack, mode); nothing is specific to a domain.
 */
import { useEffect, useState } from 'react';
import { reportOnce } from '../core/diagnostics';
import { useApp } from '../react/app';
import { useAppearance, useMode, useResolvedLayout, useStage, useTheme } from '../react/hooks';
import { sitsOn } from '../layout/geometry';
import { Canvas } from './Canvas';
import { LayoutView } from './LayoutView';
import { OverlayRootContext } from './overlay-root';
import { InactivityDialog } from './overlays/InactivityDialog';
import { ModePicker } from './overlays/ModePicker';
import { RoleBanner, RoleFrame } from './overlays/RoleBanner';
import { SceneFace } from './SceneFace';
import { SettingsSheet } from './overlays/SettingsSheet';
import { Veil } from './overlays/Veil';
import css from './Shell.module.css';

/**
 * What a moved stage leaves above it: the picture's top mirrored and blurred, the row next to the seam the picture's
 * own first one; it bleeds past the canvas so its blurred edge stays out of sight. The picture fades into it.
 */
const MIRROR_BLEED = 60;
const SEAM_PX = 32;

export function Shell() {
  const app = useApp();
  const layout = useResolvedLayout();
  const mode = useMode();
  const appearance = useAppearance();
  const theme = useTheme();
  const scene = app.scenes.get(layout.preset.scene);
  const { background } = useStage();
  const [overlayRoot, setOverlayRoot] = useState<HTMLDivElement | null>(null);
  const offsetY = layout.preset.stageOffsetY ?? 0;
  const stagePicture = scene?.stage ? background : null;
  const look = {
    ...mode.look,
    // a text box sitting right on the main bar stands off it (Composer.module.css), whatever put it there
    ...(sitsOn(layout.boxes.get('composer'), layout.boxes.get('main-bar')) ? { composer: 'apart' } : {}),
    theme: theme.id,
    scheme: appearance.scheme,
    'chat-align': appearance.chatAlign,
    ...(appearance.motion ? {} : { motion: 'reduce' }),
  };
  // the domain's brand accents, then the theme's palette for the scheme (a dark one derives its inks and
  // soft tints from the brand), then the look's own values
  const vars = {
    ...app.pack.theme,
    ...theme.palettes[appearance.scheme],
    '--chat-scale': String(appearance.chatScale),
    '--chat-width': `${appearance.chatWidth}px`,
    '--frame-padding': `${appearance.framePadding}px`,
    '--panel-padding': `${appearance.panelPadding}px`,
  };

  const speak = app.flags.speak || appearance.speakTyped;
  useEffect(() => app.voice?.setKeepOpen(speak), [app.voice, speak]);

  useEffect(() => {
    for (const p of layout.problems) reportOnce('warn', 'layout', p);
    if (layout.missingPreset) reportOnce('warn', 'layout', `preset "${layout.missingPreset}" not found, using "${layout.preset.id}"`);
    if (!scene) reportOnce('warn', 'layout', `scene "${layout.preset.scene}" not found`);
  }, [layout, scene]);

  return (
    <Canvas look={look} theme={vars} onTouch={app.kiosk.touched}>
      <OverlayRootContext.Provider value={overlayRoot}>
      <div
        className={css.scene}
        style={{ background: scene?.background ?? 'var(--color-surface-2)' }}
        data-scene={scene?.id}
        data-background={stagePicture?.image}
      >
        {stagePicture && (
          <>
            {offsetY > 0 && <div className={css.sceneMirror} style={{ backgroundImage: `url("${stagePicture.image}")`, height: offsetY + MIRROR_BLEED }} />}
            <div
              className={css.scenePicture}
              style={{
                backgroundImage: `url("${stagePicture.image}")`,
                bottom: -offsetY,
                ...(offsetY > 0 ? { maskImage: `linear-gradient(to bottom, transparent ${offsetY}px, rgb(0 0 0 / 0.35) ${offsetY + SEAM_PX / 4}px, rgb(0 0 0 / 0.8) ${offsetY + SEAM_PX / 2}px, #000 ${offsetY + SEAM_PX}px)` } : {}),
              }}
            />
          </>
        )}
      </div>
      {scene?.avatar && scene.avatarBox && (
        <SceneFace scene={{ ...scene, avatar: scene.avatar, avatarBox: { ...scene.avatarBox, y: scene.avatarBox.y + offsetY } }} />
      )}
      <RoleFrame layer="under" />
      <div className={css.regions}>
        <LayoutView node={layout.preset.root} />
      </div>
      <div className={css.overlays} ref={setOverlayRoot}>
        <RoleFrame layer="over" />
        <RoleBanner />
        <InactivityDialog />
        <ModePicker />
        <SettingsSheet />
        <Veil />
      </div>
      </OverlayRootContext.Provider>
    </Canvas>
  );
}
